import { useCallback, useState } from 'react';
import { usePaginatedData, UsePaginatedDataOptions, UsePaginatedDataResult } from './usePaginatedData';
import { getPaginatedPoets, searchAllPoets } from '@/services/poetService';
import type { Database } from '@/types/supabase';
import { PaginationParams, PaginatedResult } from '@/utils/pagination/paginationUtils';

// Define the Poet type based on the database schema
type Poet = Database['public']['Tables']['poets']['Row'];

// Cache TTL constants
const CACHE_TTL = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
};

// Cache key prefix for paginated poets - base key, page/size/sort added in service
const ALL_POETS_CACHE_KEY_BASE = 'poets:all:paginated';

export interface UseAllPoetsOptions extends Omit<UsePaginatedDataOptions<Poet>, 'fetchFn' | 'cacheKey' | 'cacheTTL'> {
  // Whether to use caching (defaults to true)
  useCache?: boolean;
  
  // Custom cache TTL (defaults to MEDIUM)
  cacheTTL?: number;
}

// Define the return type of the hook
type UseAllPoetsResult = UsePaginatedDataResult<Poet> & {
  searchPoets: (term: string) => Promise<void>;
  searchResults: Poet[];
  isSearching: boolean;
  searchError: Error | null;
  clearSearch: () => void;
  isSearchActive: boolean;
};

/**
 * Hook for fetching a paginated list of all poets, sorted by last name.
 * Uses backend pagination and sorting.
 * 
 * @param options - Configuration options for the hook
 * @returns Object containing the paginated poets data, pagination controls, loading state, and error
 */
export function useAllPoets(
  options: UseAllPoetsOptions
): UseAllPoetsResult {
  const { 
    useCache = true, 
    cacheTTL = CACHE_TTL.MEDIUM,
    ...restOptions 
  } = options;
  
  // Search state
  const [searchResults, setSearchResults] = useState<Poet[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<Error | null>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Fetch function now directly uses the getPaginatedPoets service
  const fetchPoetsBackendPaginated = useCallback(
    async (params: PaginationParams): Promise<PaginatedResult<Poet>> => {
      console.log('useAllPoets: Fetching poets via backend pagination...', params);
      
      try {
        // Call the service with pagination and sorting params
        // We always want lastName sorting for this hook now
        const result = await getPaginatedPoets({
          ...params, 
          sortBy: 'lastName' 
        });
        
        console.log(`useAllPoets: Fetched page ${params.page} (${result.data.length}/${result.metadata.totalItems} poets)`);
        
        return result; // Return the result from the service
      } catch (error) {
        console.error('useAllPoets: Error fetching paginated poets:', error);
        throw error;
      }
    },
    [] // REMOVED isSearchActive dependency - fetch logic doesn't depend on it
  );

  // Search function that uses the searchAllPoets service
  const searchPoets = useCallback(async (searchTerm: string) => {
    console.log(`[useAllPoets searchPoets] Called with term: "${searchTerm}"`);
    if (!searchTerm.trim()) {
      clearSearch(); // Use clearSearch to handle state reset
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    setIsSearchActive(true); // Mark search as active
    
    try {
      // Pass sortBy lastName to search as well
      const results = await searchAllPoets(searchTerm, 'lastName');
      setSearchResults(results);
      // If search yields results, update the main data state as well
      // This might be optional depending on desired UX - currently overrides paginated data
      // For a pure search overlay, you might skip this
    } catch (error) {
      console.error('Error searching poets:', error);
      setSearchError(error instanceof Error ? error : new Error('Unknown error searching poets'));
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []); // Removed clearSearch from dependencies to avoid potential loops if state updates trigger recalls
  
  // Function to clear search
  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setIsSearchActive(false); // Mark search as inactive
    setSearchError(null);
    // TODO: Optionally trigger a refetch of the current page after clearing search?
    // paginatedResult.refetch(); // Might be needed if search replaced data
  }, []);

  // Create a *base* cache key, specific page/sort keys handled in service
  const cacheKeyBase = useCache ? ALL_POETS_CACHE_KEY_BASE : undefined;

  // Use the paginated data hook
  // Pass the new backend-paginated fetch function
  const paginatedResult = usePaginatedData<Poet>({
    ...restOptions,
    fetchFn: fetchPoetsBackendPaginated,
    // cacheKey is now handled within the service based on params
    // Providing a base key here might interfere if usePaginatedData uses it directly
    // Let's remove it from here for now, assuming service handles caching correctly.
    // cacheKey: cacheKeyBase, // Removed
    cacheTTL // TTL is still relevant if usePaginatedData applies it
  });

  // Return both paginated data and search functionality
  return {
    ...paginatedResult,
    // Always return the paginated data from the base hook
    // The component can decide whether to show this or searchResults
    // data: isSearchActive ? searchResults : paginatedResult.data, // <-- REMOVE THIS CONDITIONAL OVERRIDE
    // Add search functionality
    searchPoets,
    searchResults,
    isSearching,
    searchError,
    clearSearch,
    isSearchActive // Also return isSearchActive so component knows
  };
} 